import React, { useEffect, useState, useRef } from 'react'
import { CircularProgress } from '@mui/material'
import { AttributionControl, MapContainer, TileLayer } from 'react-leaflet'
import 'leaflet/dist/leaflet.css'
import { loadCountriesGeoJSON } from '../../utils/geojson'
import { MapReady } from './components/MapReady'
import { CountriesAutoZoom } from './components/CountriesAutoZoom'
import { ResetViewControl } from './components/ResetViewControl'
import { CountrySetupsLayer } from './components/CountrySetupsLayer'
// COUNTRY COMPARISON DISABLED
// import { ComparisonAccordion } from './components/ComparisonAccordion'
import { EnhancedBadgeMarkers } from './components/EnhancedBadgeMarkers'
import { calculateCentroid } from './utils/calculateCentroid'
import { getEnvValue } from '../../env'

const CARTO_API_KEY = getEnvValue('REACT_APP_CARTO_API_KEY')
const CARTO_TILE_URL = `https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png?key=${encodeURIComponent(
  CARTO_API_KEY || '',
)}`

/**
 * Main map component
 */
const CountryChartsMap = ({ countries }) => {
  const [enhancedCountries, setEnhancedCountries] = useState([])
  const [loading, setLoading] = useState(true)
  const initialBoundsRef = useRef(null)

  /* COUNTRY COMPARISON DISABLED — selection state & handlers hidden for now
  const [selectedCountries, setSelectedCountries] = useState([])

  // Handle country selection/deselection
  const handleCountrySelect = country => {
    setSelectedCountries(prev => {
      const isAlreadySelected = prev.some(
        selected => selected.code === country.code,
      )
      let newSelection

      if (isAlreadySelected) {
        // Remove from selection
        newSelection = prev.filter(selected => selected.code !== country.code)
      } else {
        // Add to selection
        newSelection = [...prev, country]
      }

      return newSelection
    })
  }

  // Remove specific country from comparison
  const handleRemoveCountry = countryCode => {
    setSelectedCountries(prev =>
      prev.filter(country => country.code !== countryCode),
    )
  }
  */

  useEffect(() => {
    // Wait until the parent supplies the (already kW->MW transformed) countries
    if (!countries) return

    const fetchData = async () => {
      try {
        setLoading(true)

        // Load the GeoJSON used to position/colour the countries
        const [geoJsonData] = await Promise.all([loadCountriesGeoJSON()])

        // Enhance countries with calculated centers from GeoJSON
        const withCenters = countries.map(country => {
          // Find matching country in GeoJSON
          const geoFeature = geoJsonData.features.find(
            feature => feature.properties.ISO === country.code,
          )

          if (geoFeature) {
            // Calculate centroid from GeoJSON geometry
            const center = calculateCentroid(geoFeature.geometry)

            // Manual overrides for countries where geometric centroid isn't ideal
            const centerOverrides = {
              US: { lat: 39.5, lon: -98.35 }, // Continental US center (excludes Alaska)
              RU: { lat: 61.524, lon: 105.3188 }, // Russia center (more towards populated areas)
              CA: { lat: 56.1304, lon: -106.3468 }, // Canada center (south of true centroid)
              PT: { lat: 39.5, lon: -8.0 }, // Portugal mainland (excludes Azores & Madeira)
              ES: { lat: 40.0, lon: -3.7 }, // Spain mainland (excludes Canary Islands)
            }

            const finalCenter =
              centerOverrides[country.code] || center || country.center

            return {
              ...country,
              center: finalCenter,
            }
          }

          return country // Keep manual center if no GeoJSON match
        })

        setEnhancedCountries(withCenters)
      } catch (error) {
        console.error('Error fetching data:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [countries])

  if (loading) {
    return (
      <div
        style={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress />
      </div>
    )
  }

  return (
    <div
      style={{
        height: '100%',
        width: '100%',
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      <MapContainer
        center={[54.5, 15.0]}
        zoom={4}
        minZoom={3}
        maxZoom={10}
        maxBounds={[
          [-85, -180], // Southwest corner
          [85, 180], // Northeast corner
        ]}
        maxBoundsViscosity={1.0}
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom
        attributionControl={false}
      >
        <TileLayer
          url={CARTO_TILE_URL}
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
        />
        <AttributionControl prefix={false} />

        <MapReady>
          <ResetViewControl boundsRef={initialBoundsRef} />
          <CountriesAutoZoom
            countries={enhancedCountries}
            boundsRef={initialBoundsRef}
          />
          <CountrySetupsLayer countries={enhancedCountries} />

          <EnhancedBadgeMarkers
            countries={enhancedCountries}
            /* COUNTRY COMPARISON DISABLED
            onCountrySelect={handleCountrySelect}
            selectedCountries={selectedCountries}
            */
          />
        </MapReady>
      </MapContainer>

      {/* COUNTRY COMPARISON DISABLED
      <ComparisonAccordion
        selectedCountries={selectedCountries}
        onRemoveCountry={handleRemoveCountry}
      />
      */}

      {/* Help card in bottom-left corner — DISABLED for now
      <div
        style={{
          position: 'absolute',
          bottom: '10px',
          left: '10px',
          zIndex: 1000,
          backgroundColor: 'white',
          borderRadius: '8px',
          padding: '8px 12px',
          boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
          fontSize: '12px',
          lineHeight: '1.4',
          maxWidth: '220px',
          border: '1px solid #e0e0e0',
        }}
      >
        <div
          style={{ fontWeight: 'bold', marginBottom: '4px', color: '#368ED8' }}
        >
          How to compare countries:
        </div>
        <div style={{ color: '#666' }}>
          • Hover over badges to view clients
          <br />
          • Click badges to add/remove from comparison
          <br />
          • Right-click badges for quick view popup
          <br />• Selected countries appear in green
        </div>
      </div>
      */}
    </div>
  )
}

export default CountryChartsMap
